// ----------------------------------------------------------------------
// About page intro: image + story, the regulators we support, then the
// mission and vision cards. Two columns on md+, stacked on mobile.

import { motion } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Card, Chip, Grid, Stack, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInLeft, varFadeInRight, varFadeInUp } from '../../../components/animate';
import { SectionHeader, SectionShell } from '../common';

// ----------------------------------------------------------------------

const STORY = [
  'ASAP Marketing and Studies Consultancy is an independent training and service provider established in 2017, dedicated exclusively to the career development of healthcare professionals.',
  'We deliver complete assistance for building better opportunities in the GCC healthcare sector, covering the DOH, DHA, MOH, OMSB, QCHP, SCHC and NHRA licensing routes along with others. Our well-qualified team provides prompt guidance, a distinctive approach to teaching, and end-to-end document verification — a time-bound, cost-effective service for healthcare professionals.',
  'With a comprehensive approach to exam preparation, we have helped countless students succeed in their professional endeavours. Whether you are advancing in your current career or taking on a new challenge, ASAP Marketing and Studies Consultancy is here to help you succeed.',
];

const REGULATORS = ['DOH', 'DHA', 'MOH', 'OMSB', 'QCHP', 'SCHC', 'NHRA'];

const PILLARS = [
  {
    title: 'Mission',
    icon: 'lucide:target',
    body: 'To provide high-quality training and support that helps our clients and candidates achieve their goals, meeting evolving social and economic needs through best practice.',
  },
  {
    title: 'Vision',
    icon: 'lucide:eye',
    body: 'To be a forefront service provider in the hearts and minds of health professionals — where candidates feel heard, appreciated and valued, and the process is easy and frictionless.',
  },
];

// ----------------------------------------------------------------------

export default function AboutWidget() {
  return (
    <SectionShell>
      <Grid container spacing={{ xs: 4, md: 6 }} sx={{ alignItems: 'center' }}>
        <Grid size={{ xs: 12, md: 5 }} sx={{ order: { xs: 2, md: 1 } }}>
          <MotionInView variants={varFadeInLeft}>
            <Box
              sx={{
                position: 'relative',
                borderRadius: 3,
                p: { xs: 3, md: 4 },
                textAlign: 'center',
                overflow: 'hidden',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundImage: () =>
                  `radial-gradient(60% 55% at 85% 12%, ${alpha('#DF3E30', 0.12)} 0%, transparent 62%), radial-gradient(55% 50% at 5% 95%, ${alpha('#1877F2', 0.12)} 0%, transparent 60%)`,
              }}
            >
              <Box
                component="img"
                src="/assets/images/asap/about.png"
                alt="ASAP Marketing and Studies Consultancy"
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: { xs: 260, md: 380 },
                  objectFit: 'contain',
                  display: 'block',
                  mx: 'auto',
                  transition: 'transform 0.5s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                  '@media (hover: hover)': {
                    '&:hover': { transform: 'scale(1.04)' },
                  },
                }}
              />
            </Box>
          </MotionInView>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }} sx={{ order: { xs: 1, md: 2 } }}>
          <MotionInView variants={varFadeInRight}>
            <SectionHeader
              align="left"
              titleVariant="h3"
              titleComponent="h1"
              eyebrow="About us"
              title="About ASAP UAE"
              sx={{ mb: { xs: 2, md: 2.5 } }}
            />
          </MotionInView>

          <Stack spacing={{ xs: 1.5, md: 2 }}>
            {STORY.map((paragraph, index) => (
              <MotionInView key={paragraph} variants={varFadeInUp} transition={{ delay: index * 0.08 }}>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.85 }}>
                  {paragraph}
                </Typography>
              </MotionInView>
            ))}
          </Stack>

          <Box sx={{ mt: { xs: 2.5, md: 3 } }}>
            <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 1 }}>
              Regulators we support
            </Typography>

            <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: 'wrap' }}>
              {REGULATORS.map((regulator, index) => (
                <Box
                  component={motion.span}
                  key={regulator}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: index * 0.05, ease: 'easeOut' }}
                  sx={{ display: 'inline-flex' }}
                >
                  <Chip
                    label={regulator}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      color: 'primary.main',
                      border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={{ xs: 3, md: 4 }}
        sx={{ mt: { xs: 4, md: 6 }, alignItems: 'stretch' }}
      >
        {PILLARS.map((pillar, index) => (
          <Grid key={pillar.title} size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <MotionInView
              variants={varFadeInUp}
              transition={{ delay: index * 0.12 }}
              sx={{ width: '100%' }}
            >
              <Card
                sx={{
                  position: 'relative',
                  height: '100%',
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  boxShadow: 'none',
                  transition: 'transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
                  '@media (hover: hover)': {
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: 'transparent',
                      boxShadow: (theme) => `0 18px 36px ${alpha(theme.palette.grey[900], 0.12)}`,
                    },
                  },
                }}
              >
                <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'primary.main',
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    }}
                  >
                    <Iconify icon={pillar.icon} width={20} />
                  </Box>

                  <Typography component="h2" variant="h5" sx={{ color: 'text.primary' }}>
                    {pillar.title}
                  </Typography>
                </Stack>

                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.85 }}>
                  {pillar.body}
                </Typography>
              </Card>
            </MotionInView>
          </Grid>
        ))}
      </Grid>
    </SectionShell>
  );
}
